import { TransactionOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Popconfirm, Table } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { debtApi } from '../../../../../apis'
import { AlertMessageEnum, getDateFormat } from '../../../../../common'
import {
  LoadingDataComponent,
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { UserTypeEnum } from '../../../../../models'
import { DebtDetailEntity } from '../../../../../models/debt/debt'
import { RootState } from '../../../../../stores'
import {
  loadDebts,
  loadProfile,
  setActionProcessing,
  setAlert,
} from '../../../../../stores/actions'

type ManageDebtModalComponentProps = {
  cardId?: string
  stationId?: number
}

const StyledLabel = styled.span`
  font-weight: bold;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const FooterElement = styled.div`
  margin-right: 20px;
  font-weight: bold;
`

type SelectedTransaction = {
  cardId: string
  transactionId: number
  debtId: number
  total: number
}

export const ManageDebtModalComponent: React.FC<ManageDebtModalComponentProps> =
  ({ cardId, stationId }) => {
    const dispatch = useDispatch()
    const { profile } = useSelector((state: RootState) => state.auth)
    const { language } = useSelector((state: RootState) => state.language)
    const { management } = useSelector((state: RootState) => state.common)
    const [loading, setLoading] = React.useState(false)
    const [detailDebts, setDetailDebts] = React.useState<DebtDetailEntity[]>()
    const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })
    const [selectedTransactions, setSelectedTransactions] = React.useState<
      SelectedTransaction[]
    >([])
    const totalWillPay = React.useMemo(
      () => selectedTransactions.reduce((acc, curr) => acc + curr.total, 0),
      [selectedTransactions]
    )
    const columns = React.useMemo(
      () => [
        {
          dataIndex: 'id',
          key: 'id',
          title: translate('debt.transaction.id', language),
          width: 50,
        },
        {
          title: translate('debt.transaction.time', language),
          width: 100,
          render: (_: any, debt: DebtDetailEntity) =>
            getDateFormat(debt.transaction.time, true),
        },
        {
          title: translate('debt.transaction.fuel', language),
          width: 100,
          render: (_: any, debt: DebtDetailEntity) =>
            debt.transaction.pumpShift.pump.tank.fuel.name,
        },
        {
          title: translate('debt.transaction.volume', language),
          width: 100,
          render: (_: any, debt: DebtDetailEntity) => (
            <NumberFormat
              value={debt.transaction.volume}
              suffixKey="common.unitType"
            />
          ),
        },
        {
          title: translate('debt.transaction.unitPrice', language),
          width: 100,
          render: (_: any, debt: DebtDetailEntity) => (
            <NumberFormat
              value={debt.transaction.unitPrice}
              suffixKey="common.moneyType"
            />
          ),
        },
        {
          title: translate('debt.transaction.amount', language),
          width: 100,
          render: (_: any, debt: DebtDetailEntity) => (
            <NumberFormat
              value={debt.transaction.totalAmount}
              suffixKey="common.moneyType"
            />
          ),
        },
        {
          title: translate('debt.transaction.accountsPayable', language),
          width: 100,
          render: (_: any, debt: DebtDetailEntity) => (
            <NumberFormat
              value={debt.accountsPayable}
              suffixKey="common.moneyType"
            />
          ),
        },
      ],
      [language]
    )

    const pay = async () => {
      try {
        dispatch(setActionProcessing(true))
        dispatch(setAlert())
        await debtApi.pay(
          selectedTransactions.map((item) => ({
            ...item,
            reason: `${translate('debt.pay.reason', language)} ${
              item.transactionId
            })`,
          }))
        )
        dispatch(
          setAlert({
            key: 'debt.pay.success',
            type: AlertMessageEnum.SUCCESS,
          })
        )
        dispatch(loadDebts())
      } catch (e) {
        if (e && e.response && e.response.status === 401) {
          dispatch(loadProfile())
          await debtApi.pay(
            selectedTransactions.map((item) => ({
              ...item,
              reason: `${translate('debt.pay.reason', language)} ${
                item.transactionId
              })`,
            }))
          )
          dispatch(
            setAlert({
              key: 'debt.pay.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
          dispatch(loadDebts())
        } else {
          dispatch(
            setAlert({
              key: 'debt.pay.fail',
              type: AlertMessageEnum.ERROR,
            })
          )
        }
      } finally {
        dispatch(setActionProcessing(false))
      }
    }

    React.useEffect(() => {
      const loadData = async () => {
        if (stationId && cardId) {
          try {
            setLoading(true)
            const resp = await debtApi.getDetailDebt(cardId, stationId)
            setDetailDebts(resp.data.data)
          } catch (e) {
            if (e && e.response && e.response.status === 401) {
              dispatch(loadProfile())
              setLoading(true)
              const resp = await debtApi.getDetailDebt(cardId, stationId)
              setDetailDebts(resp.data.data)
            } else {
              dispatch(
                setAlert({
                  key: 'debt.loadData.fail',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          } finally {
            setLoading(false)
          }
        }
      }
      loadData()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const rowSelection = {
      onSelect: (record: DebtDetailEntity, selected: any) => {
        setSelectedTransactions(
          detailDebts
            ? selected
              ? [
                  ...selectedTransactions,
                  {
                    cardId: record.card.id,
                    transactionId: record.transaction.id,
                    debtId: record.id,
                    total: record.accountsPayable,
                  },
                ]
              : selectedTransactions.filter((item) => item.debtId !== record.id)
            : []
        )
      },
      onSelectAll: (selected: boolean) => {
        setSelectedTransactions(
          detailDebts && selected
            ? detailDebts.map((item) => ({
                cardId: item.card.id,
                debtId: item.id,
                transactionId: item.transaction.id,
                total: item.accountsPayable,
              }))
            : []
        )
      },
      selectedRowKeys: selectedTransactions.map((item) => item.debtId),
    }

    const body = (
      <Form>
        {loading && <LoadingDataComponent />}
        {!loading && detailDebts && !detailDebts.length && (
          <Alert
            type="info"
            message={translate('common.warning', language)}
            description={translate('debt.noData', language)}
            showIcon
          />
        )}
        {!loading && detailDebts && detailDebts.length > 0 && (
          <>
            <Form.Item
              label={
                <StyledLabel>{translate('debt.card.id', language)}</StyledLabel>
              }
            >
              {detailDebts[0].card.id}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('debt.customer', language)}
                </StyledLabel>
              }
            >
              {detailDebts[0].card.customer?.name}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>
                  {translate('debt.customer.phone', language)}
                </StyledLabel>
              }
            >
              {detailDebts[0].card.customer?.phone}
            </Form.Item>
            <Form.Item
              label={
                <StyledLabel>{translate('debt.station', language)}</StyledLabel>
              }
            >
              {`${detailDebts[0].station.name} (${detailDebts[0].station.address})`}
            </Form.Item>
            {(!management.alertMessage ||
              (management.alertMessage &&
                management.alertMessage.type !== AlertMessageEnum.SUCCESS)) && (
              <>
                <Form.Item
                  label={
                    <StyledLabel>
                      {translate('debt.selectTransactions', language)}
                    </StyledLabel>
                  }
                ></Form.Item>
                <Table
                  rowSelection={
                    management.actionProcessing ? undefined : rowSelection
                  }
                  dataSource={detailDebts.map((item) => ({
                    ...item,
                    key: item.id,
                  }))}
                  columns={columns}
                  bordered
                  pagination={
                    detailDebts.length > 5
                      ? {
                          position: ['bottomCenter'],
                          showSizeChanger: false,
                          pageSize: 5,
                        }
                      : false
                  }
                  size="small"
                  style={{ marginBottom: '20px' }}
                  footer={() =>
                    totalWillPay > 0 ? (
                      <Footer>
                        <FooterElement>
                          {`${translate('debt.totalWillPay', language)}: `}
                          <NumberFormat
                            value={totalWillPay}
                            suffixKey="common.moneyType"
                          />
                        </FooterElement>
                      </Footer>
                    ) : (
                      <></>
                    )
                  }
                />
              </>
            )}
          </>
        )}
        <ManageModalFooterComponent
          customButtons={
            (!management.alertMessage ||
              (management.alertMessage &&
                management.alertMessage.type !== AlertMessageEnum.SUCCESS)) &&
            profile &&
            profile.userType.type !== UserTypeEnum.CUSTOMER &&
            selectedTransactions.length ? (
              <Popconfirm
                title={translate('debt.pay.confirm', language)}
                okText={translate('common.yes', language)}
                cancelText={translate('common.no', language)}
                placement="topRight"
                onConfirm={pay}
              >
                <Button
                  type="primary"
                  icon={<TransactionOutlined />}
                  loading={management.actionProcessing}
                >
                  {translate(
                    `debt.${management.actionProcessing ? 'paying' : 'pay'}`,
                    language
                  )}
                </Button>
              </Popconfirm>
            ) : undefined
          }
        />
      </Form>
    )

    return (
      <ManageModalComponent
        body={body}
        width={isDesktopScreen ? '50%' : '100%'}
      />
    )
  }
